import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  IndianRupee, ArrowUpDown, Download, Calendar, RefreshCw, X,
  ChevronLeft, ChevronRight, ChevronDown, Clock, ArrowLeft, Hash, Eye, FileSpreadsheet, List, LayoutGrid, Filter,
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";

import { API, formatCurrency, formatCompact, formatDateIST, DATE_PRESETS, MODE_COLORS } from "./payments/paymentsConstants";
import { PaymentKPIs } from "./payments/PaymentKPIs";
import { PaymentCharts } from "./payments/PaymentCharts";
import { CollectionDrillDown } from "./payments/CollectionDrillDown";
import { BillingCashierView } from "./payments/BillingCashierView";

export default function PaymentsReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlFunction = searchParams.get("function");
  const [reportData, setReportData] = useState([]);
  const [grandTotals, setGrandTotals] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cashiers, setCashiers] = useState([]);
  const [chartView, setChartView] = useState("bar");
  // Two-level drill-down state
  const [drillDate, setDrillDate] = useState(null);
  const [cashierSummaryData, setCashierSummaryData] = useState(null);
  const [cashierSummaryLoading, setCashierSummaryLoading] = useState(false);
  const [drillCashier, setDrillCashier] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillSummary, setDrillSummary] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const [datePreset, setDatePreset] = useState("Today");
  const [dateFrom, setDateFrom] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [cashier, setCashier] = useState("");
  const [fnFilter, setFnFilter] = useState(() => {
    if (urlFunction && ["Pharmacy", "Hospital", "All"].includes(urlFunction)) return urlFunction;
    return "All";
  });
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [viewMode, setViewMode] = useState("collection"); // "collection" or "cashier"
  const [modeFilter, setModeFilter] = useState("");
  const [gridView, setGridView] = useState("summary"); // "summary" or "details"
  const [detailTxns, setDetailTxns] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false); // "" = all, or "cash", "card", "credit", "eft", "upi", "others"

  const buildParams = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (startTime) p.start_time = startTime;
    if (endTime) p.end_time = endTime;
    if (cashier) p.cashier = cashier;
    if (fnFilter && fnFilter !== "All") p.function = fnFilter;
    p.sort_by = sortBy;
    p.sort_order = sortOrder;
    return p;
  }, [dateFrom, dateTo, startTime, endTime, cashier, fnFilter, sortBy, sortOrder]);

  const fetchReport = useCallback(async (pg) => {
    setLoading(true);
    try {
      const params = buildParams();
      params.page = pg || page;
      params.page_size = 50;
      const { data } = await axios.get(`${API}/api/payments/collection-report`, { params });
      setReportData(data.data || []);
      const gt = data.grand_totals || {};
      // Inject advance data from order master
      const adv = data.advances || {};
      gt._advanceTotal = adv.total || 0;
      gt._advanceCount = adv.count || 0;
      gt._advanceCash = adv.cash || 0;
      gt._advanceCard = adv.card || 0;
      gt._advanceUPI = adv.upi || 0;
      gt._advanceNEFT = adv.neft_rtgs || 0;
      gt._advanceEFT = adv.eft || 0;
      gt._advanceOthers = adv.others || 0;
      setGrandTotals(gt);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      if (pg) setPage(pg);
    } catch (e) { console.error("Error fetching report:", e); }
    finally { setLoading(false); }
  }, [buildParams, page]);

  const fetchCashiers = useCallback(async () => {
    try {
      const params = {};
      if (fnFilter && fnFilter !== "All") params.function = fnFilter;
      const { data } = await axios.get(`${API}/api/payments/cashiers`, { params });
      setCashiers(data || []);
    } catch (e) { console.error("Error fetching cashiers:", e); }
  }, [fnFilter]);

  // Level 1: Fetch cashier-wise summary for a date
  const fetchCashierSummary = useCallback(async (date) => {
    setCashierSummaryLoading(true);
    setDrillCashier(null);
    setDrillData([]);
    setDrillSummary(null);
    try {
      const params = {};
      if (startTime) params.start_time = startTime;
      if (endTime) params.end_time = endTime;
      if (cashier) params.cashier = cashier;
      if (fnFilter && fnFilter !== "All") params.function = fnFilter;
      const { data } = await axios.get(`${API}/api/payments/cashier-summary/${date}`, { params });
      setCashierSummaryData(data);
      setDrillDate(date);
    } catch (e) { console.error("Error fetching cashier summary:", e); }
    finally { setCashierSummaryLoading(false); }
  }, [startTime, endTime, cashier, fnFilter]);

  // Level 2: Fetch transaction details for a specific cashier on a date
  const fetchCashierDrillDown = useCallback(async (cashierName) => {
    setDrillLoading(true);
    setDrillCashier(cashierName);
    try {
      const params = { cashier: cashierName };
      if (startTime) params.start_time = startTime;
      if (endTime) params.end_time = endTime;
      if (fnFilter && fnFilter !== "All") params.function = fnFilter;
      const { data } = await axios.get(`${API}/api/payments/drill-down/${drillDate}`, { params });
      setDrillData(data.transactions || []);
      setDrillSummary(data.summary || null);
    } catch (e) { console.error("Error fetching cashier drill-down:", e); }
    finally { setDrillLoading(false); }
  }, [startTime, endTime, fnFilter, drillDate]);

  const handleCloseDrill = useCallback(() => {
    setDrillDate(null);
    setDrillCashier(null);
    setCashierSummaryData(null);
    setDrillData([]);
    setDrillSummary(null);
  }, []);

  const handleBackToCashiers = useCallback(() => {
    setDrillCashier(null);
    setDrillData([]);
    setDrillSummary(null);
  }, []);

  useEffect(() => { fetchCashiers(); }, [fetchCashiers]);
  useEffect(() => { fetchReport(1); }, [buildParams]);

  // Fetch detailed transactions when grid view changes to "details"
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(0);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailTotalAmount, setDetailTotalAmount] = useState(0);
  const fetchDetailTxns = useCallback(async (pg) => {
    setDetailLoading(true);
    try {
      const params = buildParams();
      params.page = pg || detailPage;
      params.page_size = 100;
      if (modeFilter) params.mode = modeFilter;
      const { data } = await axios.get(`${API}/api/payments/transactions`, { params });
      setDetailTxns(data.transactions || []);
      setDetailTotal(data.total || 0);
      setDetailTotalAmount(data.total_amount || 0);
      setDetailTotalPages(data.total_pages || 0);
      if (pg) setDetailPage(pg);
    } catch (e) { console.error("Error fetching detail transactions:", e); }
    finally { setDetailLoading(false); }
  }, [buildParams, detailPage, modeFilter]);

  useEffect(() => {
    if (gridView === "details") fetchDetailTxns(1);
  }, [gridView, buildParams, modeFilter]);

  const handleGridExport = async () => {
    try {
      const params = buildParams();
      params.export_format = "xlsx";
      params.detail_level = gridView === "details" ? "detailed" : "summary";
      if (modeFilter) params.mode = modeFilter;
      const response = await axios.get(`${API}/api/payments/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const prefix = gridView === "details" ? "detailed_collection" : "collection_report";
      const link = document.createElement("a"); link.href = url; link.download = `${prefix}.xlsx`; link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Grid export error:", e); }
  };

  const handlePreset = (preset) => { setDatePreset(preset.label); const [from, to] = preset.getRange(); setDateFrom(from); setDateTo(to); };
  const handleSort = (field) => { if (sortBy === field) setSortOrder(prev => prev === "asc" ? "desc" : "asc"); else { setSortBy(field); setSortOrder("asc"); } };
  const [showExportMenu, setShowExportMenu] = useState(false);
  const handleExport = async (fmt, detailLevel = "summary") => {
    setShowExportMenu(false);
    try {
      const params = buildParams();
      params.export_format = fmt;
      params.detail_level = detailLevel;
      const response = await axios.get(`${API}/api/payments/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const prefix = detailLevel === "detailed" ? "detailed_collection_report" : "collection_report";
      const link = document.createElement("a"); link.href = url; link.download = `${prefix}.${fmt === "csv" ? "csv" : "xlsx"}`; link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Export error:", e); }
  };
  const clearFilters = () => { setDatePreset("Today"); const today = format(new Date(), "yyyy-MM-dd"); setDateFrom(today); setDateTo(today); setStartTime(""); setEndTime(""); setCashier(""); setFnFilter("All"); setModeFilter(""); };

  const pieData = useMemo(() => {
    const g = grandTotals;
    return [
      { name: "Cash", value: g.cash || 0 }, { name: "Card", value: g.card || 0 },
      { name: "Credit", value: g.credit || 0 }, { name: "UPI", value: g.upi || 0 },
      { name: "EFT", value: g.eft || 0 }, { name: "NEFT/RTGS", value: g.neft_rtgs || 0 },
      { name: "Advance", value: g.wallet || 0 }, { name: "Payout", value: g.payout || 0 },
      { name: "Others", value: g.others || 0 },
    ];
  }, [grandTotals]);

  const barData = useMemo(() =>
    reportData.map(r => ({
      date: r.date ? formatDateIST(r.date).replace(/\s\d{4}$/, "") : "",
      Cash: r.cash || 0, Card: r.card || 0, Credit: r.credit || 0,
      UPI: r.upi || 0, EFT: r.eft || 0, "NEFT/RTGS": r.neft_rtgs || 0,
      Advance: r.wallet || 0, Payout: r.payout || 0, Others: r.others || 0,
    })), [reportData]);

  // Filter summary table data based on modeFilter (client-side — all data loaded)
  const MODE_KEY_LABEL = {
    cash: "Cash", card: "Card", debit_card: "Debit Card", credit_card: "Credit Card",
    credit: "Credit", credit_only: "Credit", self_credit: "Self Credit",
    eft: "EFT", upi: "UPI",
    neft_rtgs: "NEFT/RTGS", cheque: "Cheque", netbanking: "Netbanking",
    wallet: "Wallet", payout: "Payout", others: "Others",
    collection: "Collection", debtors: "Debtors", advances: "Advances",
  };
  const filteredReportData = useMemo(() => {
    if (!modeFilter) return reportData;
    // Map credit_only/self_credit to the summary row keys
    if (modeFilter === "credit_only" || modeFilter === "self_credit") {
      return reportData.filter(r => (r[modeFilter] || 0) !== 0);
    }
    return reportData.filter(r => (r[modeFilter] || 0) !== 0);
  }, [reportData, modeFilter]);

  // Active filter descriptions for info bar
  const activeFilterParts = useMemo(() => {
    const parts = [];
    if (modeFilter) parts.push({ label: "Mode", value: MODE_KEY_LABEL[modeFilter] || modeFilter });
    if (cashier) parts.push({ label: "Cashier", value: cashier });
    if (fnFilter && fnFilter !== "All") parts.push({ label: "Function", value: fnFilter });
    if (startTime) parts.push({ label: "Start", value: startTime });
    if (endTime) parts.push({ label: "End", value: endTime });
    return parts;
  }, [modeFilter, cashier, fnFilter, startTime, endTime]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-800" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5" /></button>
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm"><IndianRupee className="w-4.5 h-4.5 text-white" /></div>
            <div>
              <h1 className="text-base font-bold text-slate-900 tracking-tight">Day Wise Collection Report</h1>
              <p className="text-xs text-slate-400 mt-0.5">{formatDateIST(dateFrom)} to {formatDateIST(dateTo)}{cashier ? ` | ${cashier}` : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded-lg p-0.5 mr-2" data-testid="view-mode-toggle">
              <button onClick={() => setViewMode("collection")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "collection" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="view-collection">Day Wise</button>
              <button onClick={() => setViewMode("cashier")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "cashier" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="view-cashier">Billing Cashier</button>
            </div>
            <div className="relative" data-testid="export-dropdown">
              <button onClick={() => setShowExportMenu(prev => !prev)}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm" data-testid="export-btn">
                <FileSpreadsheet className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
              </button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-slate-200 shadow-xl z-50 py-1 text-sm" data-testid="export-menu">
                    <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Summary (Day-Wise)</div>
                    <button onClick={() => handleExport("xlsx", "summary")} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700" data-testid="export-summary-xlsx">
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Summary Excel
                    </button>
                    <button onClick={() => handleExport("csv", "summary")} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700" data-testid="export-summary-csv">
                      <Download className="w-3.5 h-3.5 text-slate-500" /> Summary CSV
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Detailed (Transaction-Level)</div>
                    <button onClick={() => handleExport("xlsx", "detailed")} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700" data-testid="export-detailed-xlsx">
                      <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-600" /> Detailed Excel
                    </button>
                    <button onClick={() => handleExport("csv", "detailed")} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700" data-testid="export-detailed-csv">
                      <Download className="w-3.5 h-3.5 text-slate-500" /> Detailed CSV
                    </button>
                  </div>
                </>
              )}
            </div>
            <button onClick={() => fetchReport(page)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="refresh-report"><RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? "animate-spin" : ""}`} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-6 space-y-6">
        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-5" data-testid="filters-panel">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {DATE_PRESETS.map(preset => (
              <button key={preset.label} onClick={() => handlePreset(preset)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${datePreset === preset.label ? "bg-slate-900 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"}`}
                data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`}>{preset.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">From</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset("Custom"); }}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-date-from" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">To</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset("Custom"); }}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-date-to" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Start Time</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-start-time" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">End Time</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-end-time" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Function</label>
              <select value={fnFilter} onChange={e => { setFnFilter(e.target.value); setCashier(""); }}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-function">
                <option value="All">All</option>
                <option value="Pharmacy">Pharmacy</option>
                <option value="Hospital">Hospital</option>
              </select>
            </div>
            {viewMode !== "cashier" && (
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Mode</label>
              <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-mode">
                <option value="">All Modes</option>
                <optgroup label="Summary Groups">
                  <option value="collection">Collection (Net)</option>
                  <option value="debtors">Debtors</option>
                  <option value="advances">Advances</option>
                </optgroup>
                <optgroup label="Individual Modes">
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="credit">Credit</option>
                  <option value="eft">EFT</option>
                  <option value="upi">UPI</option>
                  <option value="neft_rtgs">NEFT/RTGS</option>
                  <option value="wallet">Advance (Wallet)</option>
                  <option value="payout">Payout</option>
                  <option value="others">Others</option>
                </optgroup>
              </select>
            </div>
            )}
            {viewMode !== "cashier" && (
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Cashier</label>
              <select value={cashier} onChange={e => setCashier(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all" data-testid="filter-cashier">
                <option value="">All Cashiers</option>
                {cashiers.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            )}
            <div className="flex items-end">
              <button onClick={clearFilters} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200 transition-all" data-testid="clear-filters"><X className="w-3.5 h-3.5" /> Reset</button>
            </div>
          </div>
        </div>

        {/* KPIs */}
        {viewMode === "collection" && <PaymentKPIs grandTotals={grandTotals} modeFilter={modeFilter}
          onModeClick={(mode) => setModeFilter(prev => prev === mode ? "" : mode)} />}

        {/* Charts */}
        {viewMode === "collection" && <PaymentCharts chartView={chartView} setChartView={setChartView} barData={barData} pieData={pieData} grandTotals={grandTotals} modeFilter={modeFilter} />}

        {/* Billing Cashier View */}
        {viewMode === "cashier" && <BillingCashierView dateFrom={dateFrom} dateTo={dateTo} />}

        {/* Data Table */}
        {viewMode === "collection" && (
        <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden" data-testid="collection-table">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100"><Hash className="w-4 h-4 text-slate-500" /></div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">{gridView === "details" ? "Transaction Details" : "Day Wise Details"}</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {gridView === "details"
                    ? `${detailTotal} transactions${detailTotalPages > 1 ? ` \u00b7 Page ${detailPage}/${detailTotalPages}` : ""}`
                    : `${total} days \u00b7 Click any row to view cashier-wise summary`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="grid-view-toggle">
                <button onClick={() => setGridView("summary")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${gridView === "summary" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="grid-summary-btn"><LayoutGrid className="w-3 h-3" /> Summary</button>
                <button onClick={() => setGridView("details")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${gridView === "details" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="grid-details-btn"><List className="w-3 h-3" /> Details</button>
              </div>
              <button onClick={handleGridExport}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors shadow-sm"
                data-testid="grid-export-excel"><FileSpreadsheet className="w-3 h-3" /> Excel</button>
              {gridView === "summary" && totalPages > 1 && <span className="text-xs font-medium text-slate-400 bg-slate-50 px-3 py-1.5 rounded-lg">Page {page}/{totalPages}</span>}
            </div>
          </div>
          {activeFilterParts.length > 0 && (
            <div className="px-5 py-2 bg-indigo-50/60 border-b border-indigo-100/80 flex items-center gap-2 flex-wrap" data-testid="grid-filter-info">
              <Filter className="w-3 h-3 text-indigo-500" />
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Filtered by:</span>
              {activeFilterParts.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-md text-[11px] font-medium text-indigo-700 border border-indigo-200">
                  <span className="text-indigo-400">{f.label}:</span> {f.value}
                </span>
              ))}
              <button onClick={clearFilters} className="text-[10px] text-indigo-400 hover:text-indigo-600 ml-1 underline">Clear all</button>
            </div>
          )}
          {gridView === "summary" ? (
          <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">
                    <button onClick={() => handleSort("date")} className="flex items-center gap-1 hover:text-slate-800 transition-colors" data-testid="sort-date">Date <ArrowUpDown className="w-3 h-3" /></button>
                  </TableHead>
                  {[
                    { key: "cash", label: "Cash" },
                    { key: "card", label: "Card" },
                    { key: "credit", label: "Credit" },
                    { key: "eft", label: "EFT" },
                    { key: "upi", label: "UPI" },
                    { key: "neft_rtgs", label: "NEFT/RTGS" },
                    { key: "wallet", label: "Advance" },
                    { key: "payout", label: "Payout" },
                    { key: "others", label: "Others" },
                  ].map(col => (
                    <TableHead key={col.key}
                      className={`font-semibold text-xs uppercase tracking-wider text-right ${col.key === "payout" ? "text-red-600" : ""} ${modeFilter === col.key ? "text-indigo-700 bg-indigo-50/60" : col.key !== "payout" ? "text-slate-500" : ""} ${modeFilter && modeFilter !== col.key ? "opacity-40" : ""}`}>
                      {col.label}
                    </TableHead>
                  ))}
                  <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">
                    <button onClick={() => handleSort("total")} className="flex items-center gap-1 hover:text-slate-800 transition-colors ml-auto" data-testid="sort-total">Total <ArrowUpDown className="w-3 h-3" /></button>
                  </TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-center">Txns</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-center">Bills</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>{Array.from({ length: 12 }).map((_, j) => (
                      <TableCell key={j}><div className="h-4 w-full bg-slate-100 rounded animate-pulse" /></TableCell>
                    ))}</TableRow>
                  ))
                ) : reportData.length > 0 ? (
                  <>
                    {filteredReportData.map((row) => (
                      <TableRow key={row.date} className="hover:bg-slate-50/80 cursor-pointer group transition-colors" onClick={() => fetchCashierSummary(row.date)} data-testid={`row-${row.date}`}>
                        <TableCell className="font-semibold text-slate-800">{formatDateIST(row.date)}</TableCell>
                        {[
                          { key: "cash", val: row.cash },
                          { key: "card", val: row.card },
                          { key: "credit", val: row.credit },
                          { key: "eft", val: row.eft },
                          { key: "upi", val: row.upi },
                          { key: "neft_rtgs", val: row.neft_rtgs || 0 },
                          { key: "wallet", val: row.wallet || 0 },
                          { key: "payout", val: row.payout || 0 },
                          { key: "others", val: row.others > 0 ? row.others : 0 },
                        ].map(col => (
                          <TableCell key={col.key}
                            className={`text-right tabular-nums text-sm ${col.key === "payout" ? "text-red-600 font-semibold" : ""} ${modeFilter === col.key ? "font-bold text-indigo-700 bg-indigo-50/40" : ""} ${modeFilter && modeFilter !== col.key ? "opacity-40" : ""}`}>
                            {formatCurrency(col.val)}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums text-sm font-bold text-slate-900">{formatCurrency(row.total)}</TableCell>
                        <TableCell className="text-center text-sm text-slate-500">{row.txn_count}</TableCell>
                        <TableCell className="text-center text-sm text-slate-500">{row.bill_count}</TableCell>
                        <TableCell><Eye className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors" data-testid={`drill-${row.date}`} /></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-900" data-testid="grand-total-row">
                      <TableCell className="font-bold text-sm text-white">GRAND TOTAL</TableCell>
                      {[
                        { key: "cash", val: grandTotals.cash },
                        { key: "card", val: grandTotals.card },
                        { key: "credit", val: grandTotals.credit },
                        { key: "eft", val: grandTotals.eft },
                        { key: "upi", val: grandTotals.upi },
                        { key: "neft_rtgs", val: grandTotals.neft_rtgs || 0 },
                        { key: "wallet", val: grandTotals.wallet || 0 },
                        { key: "payout", val: grandTotals.payout || 0 },
                        { key: "others", val: grandTotals.others > 0 ? grandTotals.others : 0 },
                      ].map(col => (
                        <TableCell key={col.key}
                          className={`text-right tabular-nums text-sm font-semibold ${col.key === "payout" ? "text-red-400" : ""} ${modeFilter === col.key ? "text-indigo-300 bg-indigo-900/30" : col.key !== "payout" ? "text-white" : ""} ${modeFilter && modeFilter !== col.key ? "opacity-30" : ""}`}>
                          {formatCurrency(col.val)}
                        </TableCell>
                      ))}
                      <TableCell className="text-right tabular-nums font-bold text-base text-white">{formatCurrency(grandTotals.total)}</TableCell>
                      <TableCell className="text-center text-sm font-semibold text-white">{grandTotals.txn_count}</TableCell>
                      <TableCell className="text-center text-sm font-semibold text-white">{grandTotals.bill_count}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} className="h-32 text-center">
                      <IndianRupee className="w-10 h-10 mx-auto mb-2 text-slate-200" />
                      <p className="text-slate-400 text-sm">No collection data found for selected period</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400">Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => fetchReport(page - 1)} disabled={page <= 1} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors" data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => fetchReport(page + 1)} disabled={page >= totalPages} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors" data-testid="next-page"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          </>
          ) : (
          /* Details (Transaction-Level) View */
          <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Date</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Time</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Cashier</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Bill No</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Order No</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Type</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Patient</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">MRN</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Scheme</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Category</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Mode</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                  <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>{Array.from({ length: 13 }).map((_, j) => (
                      <TableCell key={j}><div className="h-4 w-full bg-slate-100 rounded animate-pulse" /></TableCell>
                    ))}</TableRow>
                  ))
                ) : detailTxns.length > 0 ? (
                  <>
                    {detailTxns.map((txn, idx) => {
                      const isPayout = txn.status === "PAYOUT";
                      return (
                        <TableRow key={idx} className={`hover:bg-slate-50/80 ${isPayout ? "bg-red-50/40" : ""}`} data-testid={`detail-txn-${idx}`}>
                          <TableCell className="text-xs text-slate-600">{txn.date}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-500">{txn.time}</TableCell>
                          <TableCell className="text-xs text-slate-600">{txn.cashierName}</TableCell>
                          <TableCell className="font-mono text-xs text-indigo-600 font-medium">{txn.billNo || "-"}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-500 max-w-[140px] truncate">{txn.orderNumber || "-"}</TableCell>
                          <TableCell className="text-xs">{txn.orderType || "-"}</TableCell>
                          <TableCell className="text-xs text-slate-700">{txn.patientName || "-"}</TableCell>
                          <TableCell className="text-xs text-slate-500">{txn.mrn || "-"}</TableCell>
                          <TableCell className="text-xs">{txn.scheme || "-"}</TableCell>
                          <TableCell>
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: (MODE_COLORS[txn.category] || "#94a3b8") + "18", color: MODE_COLORS[txn.category] || "#64748b" }}>{txn.category}</span>
                          </TableCell>
                          <TableCell className="text-[10px] text-slate-400 font-mono">{txn.mode}</TableCell>
                          <TableCell className={`text-right tabular-nums text-sm font-semibold ${isPayout ? "text-red-600" : "text-slate-800"}`}>{formatCurrency(txn.amount)}</TableCell>
                          <TableCell>
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              txn.status === "PAID" ? "bg-emerald-50 text-emerald-700" :
                              txn.status === "PAYOUT" ? "bg-red-50 text-red-700 border border-red-200" :
                              "bg-amber-50 text-amber-700"
                            }`}>{txn.status}</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-slate-900">
                      <TableCell colSpan={11} className="font-bold text-sm text-white">TOTAL ({detailTotal} transactions)</TableCell>
                      <TableCell className="text-right tabular-nums font-bold text-base text-white">{formatCurrency(detailTotalAmount)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={13} className="h-32 text-center">
                      <IndianRupee className="w-10 h-10 mx-auto mb-2 text-slate-200" />
                      <p className="text-slate-400 text-sm">No transactions found for selected period</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {gridView === "details" && detailTotalPages > 1 && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400">Page {detailPage} of {detailTotalPages} ({detailTotal} transactions)</span>
              <div className="flex gap-1">
                <button onClick={() => fetchDetailTxns(detailPage - 1)} disabled={detailPage <= 1} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => fetchDetailTxns(detailPage + 1)} disabled={detailPage >= detailTotalPages} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          </>
          )}
        </div>
        )}

        {/* Drill-Down Modal */}
        {viewMode === "collection" && (
        <CollectionDrillDown
          drillDate={drillDate}
          onClose={handleCloseDrill}
          cashierSummaryData={cashierSummaryData}
          cashierSummaryLoading={cashierSummaryLoading}
          drillCashier={drillCashier}
          drillData={drillData}
          drillSummary={drillSummary}
          drillLoading={drillLoading}
          onCashierClick={fetchCashierDrillDown}
          onBackToCashiers={handleBackToCashiers}
          startTime={startTime}
          endTime={endTime}
          cashier={cashier}
          fnFilter={fnFilter}
        />
        )}
      </main>
    </div>
  );
}
